import { useEffect } from 'react';
import { useRouter } from 'next/router';
import { NextSeo } from 'next-seo';
import Head from 'next/head';
import styled from 'styled-components';
import * as gtag from 'lib/gtag';

import { useStructuredData, useTranslation } from '@hooks';
import { capitalizeSentences, convertCategoryName, getID } from '@utils';
import { ProductServices } from '@services';
import {
    ProductPageSkeleton,
    Product,
    ShopFeatures,
} from '@components/products';
import { NewButton, TrendOfWeeks } from '@components/ui';
import { ChevronRightIcon } from '@components/icons';
import { Caption } from '@components/styles/Typography';
import * as fbq from '../../lib/fbPixel';

const ProductPage = ({ data }) => {
    const { t, locale } = useTranslation();
    const router = useRouter();

    const { data: product } = data;
    const { id, price } = product;

    const [breadcrumbData, productStructuredData] = useStructuredData(product);

    useEffect(() => {
        fbq.event('ViewContent', {
            content_ids: product?.id?.toString(),
            content_type: 'product',
            value: product.discountedPrice?.USD,
            content_name: product?.name?.tr,
            content_category: product?.category[0]?.name[locale],
            currency: 'USD',
        });
        gtag.event({
            action: 'view_item',
            value: product?.discounted_price?.TRY
                ? product?.discounted_price?.TRY
                : price?.TRY,
            items: [
                {
                    id,
                    name: product?.name.tr,
                    discount: product?.discount_rate
                        ? product?.discount_rate
                        : 0,
                    brand: 'Fervente',
                    category: product?.category[0]?.name?.tr,
                    variant: product?.colour?.name?.tr,
                    price: product?.discounted_price?.TRY
                        ? product?.discounted_price?.TRY
                        : price?.TRY,
                    currency: 'TRY',
                    quantity: product?.stock,
                },
            ],
        });
    });

    if (!data) {
        return <ProductPageSkeleton />;
    }

    const parentSlug = product?.category[0]?.parent?.slug[locale];
    const parentName = product?.category[0]?.parent?.name[locale];
    const subCategorySlug = product?.category[0]?.slug[locale];
    const subCategoryName = product?.category[0]?.name[locale];

    return (
        <>
            <Head>
                <script
                    type="application/ld+json"
                    async
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify(breadcrumbData),
                    }}
                />
                <script
                    type="application/ld+json"
                    async
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify(productStructuredData),
                    }}
                />
                <link
                    rel="preload"
                    as="image"
                    href={product?.images[0]?.urls.original}
                />
            </Head>
            <NextSeo
                title={`${capitalizeSentences(
                    product.name[locale],
                )} | ${convertCategoryName(
                    product?.category[0]?.name[locale],
                    true,
                )} | Fervente`}
                description={`Fervente | ${capitalizeSentences(
                    product.name[locale],
                )} | ${convertCategoryName(
                    product?.category[0]?.name[locale],
                    true,
                )}  | ${t('shopNowMetaDesc')}`}
                canonical={`https://www.ferventeshop.com/urun/${product.slug[locale]}`}
                openGraph={{
                    type: 'product',
                    url: `https://www.ferventeshop.com/`,
                    title: `${capitalizeSentences(
                        product.name[locale],
                    )} | ${convertCategoryName(
                        product?.category[0]?.name[locale],
                        true,
                    )} | Fervente`,
                    description: `Fervente | ${capitalizeSentences(
                        product.name[locale],
                    )} | ${convertCategoryName(
                        product?.category[0]?.name[locale],
                        true,
                    )} ${t('metaTitle')} ${t('inFervente')}. ${t(
                        'shopNowMetaDesc',
                    )}`,
                    site_name: 'ferventeshop.com',
                    images: [
                        {
                            url: product?.images[0]?.urls.medium,
                            width: 800,
                            height: 600,
                            alt: 'Og Image Alt',
                        },
                    ],
                }}
            />
            <Product product={product} />
            <TrendOfWeeks />
            <MainCategoryButtonWrapper>
                <NewButton
                    size="xlarge"
                    type="primary"
                    handleClick={() =>
                        router.push(`/${parentSlug || subCategorySlug}`)
                    }
                >
                    <Caption color="secondary">
                        {parentName || subCategoryName}
                    </Caption>
                    <ChevronRightIcon />
                </NewButton>
            </MainCategoryButtonWrapper>
            <ShopFeatures />
        </>
    );
};
const MainCategoryButtonWrapper = styled.div`
    width: 100%;
    display: none;
    justify-content: center;
    align-items: center;
    padding: 45px 15px;

    svg {
        margin-left: 10px;
    }

    @media (max-width: 1024px) {
        display: flex;
    }
`;
export async function getServerSideProps({ params, res }) {
    res.setHeader(
        'Cache-Control',
        'public, s-maxage=10, stale-while-revalidate=59',
    );
    const { slug } = params;
    const productID = getID(slug);
    const data = await ProductServices.getSingleProduct(productID);

    if (!data?.data?.id) {
        return {
            notFound: true,
        };
    }

    return {
        props: { data, key: productID }, // will be passed to the page component as props
    };
}
export default ProductPage;
